/**
 * <crewcup-upsells> — optional accessory add-ons under the 20oz CrewCup buy box.
 * Markup: blocks/crewcup-accessory-upsells.liquid. Switch: Theme settings →
 * CrewCup add-ons (enable_crewcup_accessory_upsells).
 *
 * ADDED WITHOUT OPENING THE CART. The shopper is still deciding on the cup, so an
 * add-on has to land in the cart quietly. Two things would otherwise open it:
 *
 *   1. Rebuy wraps window.fetch and sees every /cart/add.js call, and Smart Cart
 *      opens itself on any add unless Rebuy.SmartCart.skip_open is set. Rebuy's own
 *      widgets set skip_open before their add and delete it 500ms after; this does
 *      the same. skip_open does not clear itself, so it is always released — and it
 *      is left alone if something else (the popup deferral in
 *      snippets/rebuy-native-cart.liquid) already set it.
 *   2. The theme's cart drawer auto-opens on a CartLinesUpdateEvent with action
 *      'add' (assets/cart-drawer.js). This never dispatches one.
 *
 * The header count is set directly from /cart.js through <cart-icon>'s own
 * renderCartBubble(), the same call rebuy-native-cart uses, so it is right with or
 * without Rebuy.
 *
 * SEPARATE LINES, NO PROPERTIES. Each add-on is posted as its own item with only an
 * id and a quantity of 1. Nothing here sits inside the product form or carries a
 * name/form attribute, so none of it can ride along with the CrewCup's own submit.
 *
 * NO GUESSED VARIANTS. A product with more than one variant (a charm, a boot colour)
 * opens a chooser with nothing preselected, and its Add button stays disabled until
 * the shopper picks one.
 */

import { StandardEvents } from '@shopify/events';

const SKIP_OPEN_RELEASE_MS = 500;
// If Rebuy never reports the add (not loaded, or a changed event name), skip_open
// is still released after this — a stuck flag would silence the cart for the rest
// of the session.
const SKIP_OPEN_FALLBACK_MS = 1500;
const SYNC_DEBOUNCE_MS = 150;

class CrewcupUpsells extends HTMLElement {
  #pending = false;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #syncTimer = null;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);
    // Smart Cart removals and quantity changes (the drawer shoppers actually see).
    document.addEventListener('rebuy:cart.change', this.#queueSync);
    // The theme's own cart page and fallback drawer.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#queueSync);
    // Back/forward cache: the cart may have changed on another page.
    window.addEventListener('pageshow', this.#handlePageShow);

    for (const dialog of this.querySelectorAll('dialog')) {
      dialog.addEventListener('click', this.#handleBackdropClick);
      dialog.addEventListener('close', this.#handleDialogClose);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
    document.removeEventListener('rebuy:cart.change', this.#queueSync);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#queueSync);
    window.removeEventListener('pageshow', this.#handlePageShow);
    if (this.#syncTimer) clearTimeout(this.#syncTimer);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-cc-up-action]') : null;
    if (!(target instanceof HTMLElement) || !this.contains(target)) return;

    const card = target.closest('[data-cc-up-card]');
    if (!(card instanceof HTMLElement)) return;

    switch (target.dataset.ccUpAction) {
      case 'add':
        if (card.dataset.added === 'true') return;
        this.#add(card, target.dataset.variantId, target);
        break;
      case 'choose':
        if (card.dataset.added === 'true') return;
        this.#openChooser(card);
        break;
      case 'confirm': {
        const picked = card.querySelector('input[data-cc-up-variant]:checked');
        if (!(picked instanceof HTMLInputElement)) return;
        this.#add(card, picked.value, target, picked.dataset.title);
        break;
      }
      case 'close':
        card.querySelector('dialog')?.close();
        break;
    }
  };

  /** Enables the chooser's Add button once a variant is picked. */
  #handleChange = (/** @type {Event} */ event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-cc-up-variant]')) return;
    const card = input.closest('[data-cc-up-card]');
    const confirm = card?.querySelector('[data-cc-up-action="confirm"]');
    if (confirm instanceof HTMLButtonElement) confirm.disabled = false;
    this.#clearError(card);
  };

  /** Clicking the dimmed area outside the sheet closes it. */
  #handleBackdropClick = (/** @type {MouseEvent} */ event) => {
    if (event.target === event.currentTarget && event.currentTarget instanceof HTMLDialogElement) {
      event.currentTarget.close();
    }
  };

  /** Every open starts from nothing selected — never a remembered or default pick. */
  #handleDialogClose = (/** @type {Event} */ event) => {
    const dialog = event.currentTarget;
    if (!(dialog instanceof HTMLDialogElement)) return;
    for (const input of dialog.querySelectorAll('input[data-cc-up-variant]')) {
      if (input instanceof HTMLInputElement) input.checked = false;
    }
    const confirm = dialog.querySelector('[data-cc-up-action="confirm"]');
    if (confirm instanceof HTMLButtonElement) confirm.disabled = true;
  };

  #handlePageShow = (/** @type {PageTransitionEvent} */ event) => {
    if (event.persisted) this.#queueSync();
  };

  /** @param {HTMLElement} card */
  #openChooser(card) {
    const dialog = card.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
    this.#clearError(card);
    dialog.showModal();
  }

  /**
   * @param {HTMLElement} card
   * @param {string | undefined} variantId
   * @param {HTMLElement} button
   * @param {string} [chosenTitle]
   */
  async #add(card, variantId, button, chosenTitle) {
    if (this.#pending || !variantId) return;
    this.#pending = true;
    this.#clearError(card);
    this.#setBusy(button, true);
    const release = this.#holdSmartCartClosed();

    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      // The add is done the moment it answers; everything after is local UI.
      release();
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        // Shopify's own wording ("…is already sold out", "You can't add more…").
        throw new Error(data.description || data.message || 'This item could not be added. Please try again.');
      }

      card.querySelector('dialog')?.close();
      this.#markAdded(card, chosenTitle);
      this.#announce(card, `${card.dataset.productTitle || 'Item'} added to your cart`);
      await this.#sync();
    } catch (error) {
      this.#showError(card, error instanceof Error ? error.message : String(error));
    } finally {
      this.#setBusy(button, false);
      this.#pending = false;
      release();
    }
  }

  /**
   * Suppresses Smart Cart's open-on-add for this one add, then releases it.
   * @returns {() => void} Call once the add has finished.
   */
  #holdSmartCartClosed() {
    const smartCart = window.Rebuy?.SmartCart;
    // Not loaded, or someone else already holds it: leave it entirely alone.
    if (!smartCart || smartCart.skip_open) return () => {};

    // KEEP THIS WINDOW SHORT. While skip_open is set, snippets/rebuy-native-cart.liquid
    // (showSmartCart) refuses to open the cart for ANY reason — including the
    // shopper's main Add to Cart. So it is released 500ms after Rebuy has seen the
    // add (Rebuy's own figure), and the listener goes on BEFORE the add: Rebuy can
    // report the change while our response is still being read.
    smartCart.skip_open = true;
    let rebuySawChange = false;
    let addFinished = false;
    let released = false;
    /** @type {ReturnType<typeof setTimeout> | null} */
    let fallback = null;

    const release = () => {
      if (released) return;
      released = true;
      document.removeEventListener('rebuy:cart.change', onRebuyChange);
      if (fallback) clearTimeout(fallback);
      setTimeout(() => {
        if (window.Rebuy?.SmartCart === smartCart) delete smartCart.skip_open;
      }, SKIP_OPEN_RELEASE_MS);
    };
    const onRebuyChange = () => {
      rebuySawChange = true;
      if (addFinished) release();
    };
    document.addEventListener('rebuy:cart.change', onRebuyChange);

    return () => {
      if (addFinished) return;
      addFinished = true;
      if (rebuySawChange) release();
      else fallback = setTimeout(release, SKIP_OPEN_FALLBACK_MS);
    };
  }

  #queueSync = () => {
    if (this.#syncTimer) clearTimeout(this.#syncTimer);
    this.#syncTimer = setTimeout(() => this.#sync(), SYNC_DEBOUNCE_MS);
  };

  /**
   * Re-reads the cart: marks cards added or back to their CTA, updates the header
   * count, and flags whether any add-on is in the cart (which hides Buy it now —
   * see the block's stylesheet).
   */
  async #sync() {
    let cart;
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      cart = await response.json();
    } catch {
      return;
    }

    const inCart = new Set((cart.items || []).map((/** @type {{product_id: number}} */ item) => String(item.product_id)));
    let anyInCart = false;

    for (const card of this.querySelectorAll('[data-cc-up-card]')) {
      if (!(card instanceof HTMLElement)) continue;
      // A design-chooser card stands for a whole list of products (data-product-ids);
      // it counts as added when any of them is in the cart.
      const ids = (card.dataset.productIds || card.dataset.productId || '').split(',').filter(Boolean);
      const present = ids.some((id) => inCart.has(id));
      if (present) anyInCart = true;
      if (present && card.dataset.added !== 'true') this.#markAdded(card);
      if (!present && card.dataset.added === 'true') this.#reset(card);
    }

    this.toggleAttribute('data-addons-in-cart', anyInCart);

    const cartIcon = /** @type {any} */ (document.querySelector('cart-icon'));
    if (cartIcon && typeof cartIcon.renderCartBubble === 'function' && typeof cart.item_count === 'number') {
      cartIcon.renderCartBubble(cart.item_count);
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {string} [chosenTitle]
   */
  #markAdded(card, chosenTitle) {
    card.dataset.added = 'true';
    this.toggleAttribute('data-addons-in-cart', true);
    const cta = card.querySelector('[data-cc-up-cta]');
    if (cta instanceof HTMLElement) {
      cta.textContent = cta.dataset.addedLabel || '✓ Added';
      cta.setAttribute('aria-disabled', 'true');
    }
    const note = card.querySelector('[data-cc-up-note]');
    if (note instanceof HTMLElement) {
      note.textContent = chosenTitle ? chosenTitle : '';
      note.hidden = !chosenTitle;
    }
  }

  /** @param {HTMLElement} card */
  #reset(card) {
    delete card.dataset.added;
    const cta = card.querySelector('[data-cc-up-cta]');
    if (cta instanceof HTMLElement) {
      cta.textContent = cta.dataset.defaultLabel || '+ Add';
      cta.removeAttribute('aria-disabled');
    }
    const note = card.querySelector('[data-cc-up-note]');
    if (note instanceof HTMLElement) {
      note.textContent = '';
      note.hidden = true;
    }
  }

  /**
   * @param {HTMLElement} button
   * @param {boolean} busy
   */
  #setBusy(button, busy) {
    if (busy) {
      button.dataset.idleLabel = button.textContent || '';
      button.textContent = 'Adding…';
      button.setAttribute('aria-busy', 'true');
      if (button instanceof HTMLButtonElement) button.disabled = true;
    } else {
      if (button.textContent === 'Adding…') button.textContent = button.dataset.idleLabel || '';
      button.removeAttribute('aria-busy');
      // The chooser's Add stays enabled only if a variant is still picked.
      if (button instanceof HTMLButtonElement) {
        button.disabled =
          button.dataset.ccUpAction === 'confirm' &&
          !button.closest('dialog')?.querySelector('input[data-cc-up-variant]:checked');
      }
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {string} message
   */
  #showError(card, message) {
    const dialog = card.querySelector('dialog');
    const slot = (dialog?.open ? dialog : card).querySelector('[data-cc-up-error]');
    if (slot instanceof HTMLElement) {
      slot.textContent = message;
      slot.hidden = false;
    }
  }

  /** @param {Element | null | undefined} card */
  #clearError(card) {
    for (const slot of card?.querySelectorAll('[data-cc-up-error]') ?? []) {
      if (slot instanceof HTMLElement) {
        slot.textContent = '';
        slot.hidden = true;
      }
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {string} message
   */
  #announce(card, message) {
    const live = this.querySelector('[data-cc-up-live]');
    if (live instanceof HTMLElement) live.textContent = message;
  }
}

if (!customElements.get('crewcup-upsells')) {
  customElements.define('crewcup-upsells', CrewcupUpsells);
}
